import React, { useCallback, useState } from "react";
import { Snackbar, Slide, IconButton, Box, Typography, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

const SEVERITY_CONFIG = {
  success: {
    icon: CheckCircleOutlineIcon,
    color: "var(--color-success, #10b981)",
  },
  error: {
    icon: ErrorOutlineIcon,
    color: "var(--color-error, #ef4444)",
  },
  warning: {
    icon: WarningAmberIcon,
    color: "var(--color-warning, #f59e0b)",
  },
  info: {
    icon: InfoOutlinedIcon,
    color: "var(--color-info, #6366f1)",
  },
};

const DEFAULT_DURATION = {
  success: 3000,
  info: 3000,
  warning: 6000,
  error: 6000,
};

function SlideDown(props) {
  return <Slide {...props} direction="down" />;
}

export function StyledSnackbar({
  open,
  message,
  severity = "success",
  onClose,
  autoHideDuration,
  loading = false,
  hideClose = false,
}) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.success;
  const Icon = config.icon;
  const duration =
    autoHideDuration === undefined
      ? DEFAULT_DURATION[severity] ?? DEFAULT_DURATION.success
      : autoHideDuration;

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={(_, reason) => {
        if (reason !== "clickaway") onClose?.();
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      TransitionComponent={SlideDown}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 240,
          maxWidth: 480,
          px: 2,
          py: 1.25,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)",
        }}
      >
        {loading ? (
          <CircularProgress size={18} sx={{ color: config.color, flexShrink: 0 }} />
        ) : (
          <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
        )}
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "text.primary",
            lineHeight: 1.45,
            flex: 1,
            minWidth: 0,
          }}
        >
          {message}
        </Typography>
        {!hideClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              p: 0.25,
              ml: 0.5,
              flexShrink: 0,
              color: "text.disabled",
              "&:hover": { color: "text.secondary", bgcolor: "action.hover" },
            }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    </Snackbar>
  );
}

export function useSnackbar() {
  const [state, setState] = useState({ open: false, message: "", severity: "success" });

  const show = useCallback((message, severity = "success") => {
    setState({ open: true, message, severity });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const snackbarProps = {
    open: state.open,
    message: state.message,
    severity: state.severity,
    onClose: close,
  };

  return { show, close, snackbarProps };
}
